













































































































































































































import Vue from "vue";

// @ts-ignore
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import {
  CEFR_J_LEVELS,
  ROUTES,
  VUEX_LEARNING_HISTORY_ACTION_NAMES,
  VUEX_LEARNING_HISTORY_MUTATION_NAMES,
  VUEX_LEARNING_HISTORY_NAMESPACE,
  VUEX_ROOT_MUTATION_NAMES
} from "@tt-app/constants";
import { DropdownOption, LearningHistoryStatItem } from "@tt-app/models";

import { dateToYYYYMMDD, roundToDecimal, getHoursAndMinutes } from "@tt-app/utils";

import LearningHistoryAchievement from "@tt-app/components/learningHistory/LearningHistoryAchievement.vue";
import LearningHistoryDoughnutChart from "@tt-app/components/learningHistory/LearningHistoryDoughnutChart.vue";
import LearningHistoryStatListItem from "@tt-app/components/learningHistory/LearningHistoryStatListItem.vue";
import LearningHistoryProgressItem from "@tt-app/components/learningHistory/LearningHistoryProgressItem.vue";

import iconAward from "@joyz/app-core/assets/icons/default/Award.svg";
import iconBookTime from "@joyz/app-core/assets/icons/default/BookTime.svg";
import iconCalendar from "@joyz/app-core/assets/icons/default/Calendar.svg";
import iconChart from "@joyz/app-core/assets/icons/default/Chart.svg";
import iconChevronDown from "@joyz/app-core/assets/icons/transparent/ChevronDown.svg";
import iconMicrophone from "@joyz/app-core/assets/icons/default/Microphone.svg";

const {
  ACHIEVEMENT_REQUEST,
  ACTIVITY_SUMMARY_REQUEST,
  PERFORMANCE_SUMMARY_REQUEST
} = VUEX_LEARNING_HISTORY_ACTION_NAMES;

interface CefrDistribution {
  cefrj: number;
  proportion: number;
}

interface AchievementDetails {
  exerciseType: number;
  count: number;
}

interface LearnedBadges {
  details: AchievementDetails[];
  total: number;
  type: number;
}

interface Trophy {
  details: AchievementDetails[];
  total: number;
  trophy: number;
}

interface MinMaxValueObject {
  max: number;
  min: number;
  value: number;
}

interface ChartDataset {
  backgroundColor: string[];
  data: number[];
}

interface ChartData {
  datasets: ChartDataset[];
  labels: string[];
}

interface Data {
  endDate: Date;
  selectedExerciseType: number;
  startDate: Date;
}

interface LegendEntry {
  backgroundColor: string;
  label: string;
}

interface Methods {
  fetchAchievements: () => void;
  fetchActivitySummary: () => void;
  fetchLearningHistory: () => void;
  fetchPerformanceSummary: () => void;
  openAllExerciseLogs: () => void;
  openExerciseLogs: () => void;
  openExerciseLogsFromStatItem: (log: LearningHistoryStatItem) => void;
  weeksAgo: (weeksAgo: number) => Date;
}

interface Computed {
  averageRetry: MinMaxValueObject;
  cefrBackgroundColors: string[];
  cefrDistribution: number[];
  chartData: ChartData;
  chartStyles: Partial<CSSStyleDeclaration>;
  exerciseCounts: number;
  exerciseTypes: DropdownOption[];
  learnedBadges: LearnedBadges;
  legendEntries: LegendEntry[];
  performanceHidden: boolean;
  reactionTime: MinMaxValueObject;
  trophies: Trophy[];
  wordsPerMinute: MinMaxValueObject;
  totalStudyTime: string;
}

export default Vue.extend<Data, Methods, Computed, {}>({
  name: "LearningHistorySummary",
  components: {
    DatePicker,
    LearningHistoryAchievement,
    LearningHistoryDoughnutChart,
    LearningHistoryStatListItem,
    LearningHistoryProgressItem
  },
  filters: {
    displayDate(date: Date) {
      if (!date || typeof date !== "object") {
        return "";
      }

      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return `${year}/${month}/${day}`;
    }
  },
  data() {
    const storedEndDate = this.$store.getters["learning_history_end_date"];
    const storedStartDate = this.$store.getters["learning_history_start_date"];

    let endDate = new Date();
    let startDate = new Date(new Date().valueOf() - 60 * 60 * 24 * 14 * 1000);

    // TODO:: Make this dynamic / stored when we add more options.
    const selectedExerciseType = 1;

    if (storedStartDate) {
      const maximumStartDate = new Date();

      if (storedStartDate > maximumStartDate) {
        startDate = maximumStartDate;
      } else {
        startDate = storedStartDate;
      }
    }

    if (storedEndDate) {
      const minimumEndDate = startDate;
      const maximumEndDate = new Date();

      if (storedEndDate <= startDate) {
        endDate = minimumEndDate;
      } else if (storedEndDate > maximumEndDate) {
        endDate = maximumEndDate;
      } else {
        endDate = storedEndDate;
      }
    }

    return {
      endDate: endDate,
      icons: {
        award: iconAward,
        microphone: iconMicrophone,
        bookTime: iconBookTime,
        calendar: iconCalendar,
        chart: iconChart,
        chevronDown: iconChevronDown
      },
      selectedExerciseType: selectedExerciseType,
      startDate: startDate
    };
  },
  computed: {
    averageRetry() {
      return this.$store.state.learningHistory.averageRetry;
    },
    cefrBackgroundColors() {
      return [
        "#93a8ac",
        "#6597a8",
        "#85c7de",
        "#b5ddeb",
        "#7be0ad",
        "#afeccd",
        "#7a82ab",
        "#afb4cc",
        "#ffd07b",
        "#ffe2af",
        "#ff9a3a",
        "#ffc288"
      ];
    },
    cefrDistribution() {
      const distribution = this.$store.state.learningHistory.exerciseCefrDistribution;

      if (!distribution) {
        return [];
      }

      return distribution.reduce((accumulator: number[], currentValue: CefrDistribution) => {
        const value = currentValue.proportion;
        const roundedValue = roundToDecimal(value * 100, 0);
        accumulator.push(roundedValue);
        return accumulator;
      }, []);
    },
    chartData() {
      return {
        datasets: [
          {
            backgroundColor: this.cefrBackgroundColors,
            data: this.cefrDistribution
          }
        ],
        labels: CEFR_J_LEVELS.reduce((accumulator: string[], level) => {
          accumulator.push(level.display);
          return accumulator;
        }, [])
      };
    },
    chartStyles() {
      return {
        height: "220px",
        margin: "0 auto 16px",
        position: "relative",
        width: "222px"
      };
    },
    exerciseCounts() {
      return this.$store.state.learningHistory.exerciseCounts;
    },
    exerciseTypes() {
      const enabledExerciseTypes = [1];

      return enabledExerciseTypes.map(exerciseType => {
        const functionString = this.$__(`exercise.function.${exerciseType}`);
        const typeString = this.$__(`exercise.type.${exerciseType}`);

        return {
          label: `${functionString} ${typeString}`,
          value: exerciseType
        };
      });
    },
    learnedBadges() {
      return this.$store.state.learningHistory.learnedBadges;
    },
    legendEntries() {
      const colors = this.cefrBackgroundColors;
      const labels = CEFR_J_LEVELS;

      const entries = this.cefrDistribution.map((proportion, index) => {
        if (!proportion) {
          return undefined;
        }

        return {
          backgroundColor: colors[index],
          label: labels[index].display
        };
      });

      return entries.filter(Boolean) as LegendEntry[];
    },
    performanceHidden() {
      const cefrDistributionExists = this.cefrDistribution.length > 0;
      const averageRetryExists = this.averageRetry.value !== null;
      const wordsPerMinuteExists = this.wordsPerMinute.value !== null;
      const reactionTimeExists = this.reactionTime.value !== null;

      return (
        !cefrDistributionExists ||
        !averageRetryExists ||
        !wordsPerMinuteExists ||
        !reactionTimeExists
      );
    },
    reactionTime() {
      const rawData = { ...this.$store.state.learningHistory.reactionTime };

      rawData.scale[0] = rawData.scale[0] / 1000;
      rawData.scale[1] = rawData.scale[1] / 1000;
      rawData.value = rawData.value / 1000;

      return rawData;
    },
    trophies() {
      return this.$store.state.learningHistory.trophies;
    },
    wordsPerMinute() {
      return this.$store.state.learningHistory.wordsPerMinute;
    },
    totalStudyTime() {
      const time = ((this.exerciseCounts as unknown) as LearningHistoryStatItem[]).reduce(
        (runningTotal: number, exerciseCount: LearningHistoryStatItem) =>
          runningTotal + exerciseCount.studyTime,
        0
      );
      const { hours, minutes } = getHoursAndMinutes(time);
      const hourString: string = hours ? this.$__("common.hours", hours) : "";
      const minuteString: string = this.$__("common.minutes", minutes);
      return `${hourString}${minuteString}`;
    }
  },
  watch: {
    endDate: {
      handler(endDate) {
        if (!endDate) {
          return;
        }

        this.$store.commit(`${VUEX_ROOT_MUTATION_NAMES.LEARNING_HISTORY_END_DATE}`, endDate);
        this.fetchLearningHistory();
      },
      immediate: false
    },
    startDate: {
      handler(startDate) {
        if (!startDate) {
          return;
        }

        this.$store.commit(`${VUEX_ROOT_MUTATION_NAMES.LEARNING_HISTORY_START_DATE}`, startDate);
        this.fetchLearningHistory();
      },
      immediate: false
    }
  },
  created() {
    this.fetchLearningHistory();
  },
  methods: {
    fetchLearningHistory() {
      this.fetchAchievements();
      this.fetchActivitySummary();
      this.fetchPerformanceSummary();
    },
    fetchAchievements() {
      if (!this.startDate || !this.endDate) {
        return;
      }

      this.$store.dispatch(`${VUEX_LEARNING_HISTORY_NAMESPACE}/${ACHIEVEMENT_REQUEST}`, {
        startDate: dateToYYYYMMDD(this.startDate),
        endDate: dateToYYYYMMDD(this.endDate)
      });
    },
    fetchActivitySummary() {
      if (!this.startDate || !this.endDate) {
        return;
      }

      this.$store.dispatch(`${VUEX_LEARNING_HISTORY_NAMESPACE}/${ACTIVITY_SUMMARY_REQUEST}`, {
        startDate: dateToYYYYMMDD(this.startDate),
        endDate: dateToYYYYMMDD(this.endDate)
      });
    },
    fetchPerformanceSummary() {
      if (!this.startDate || !this.endDate) {
        return;
      }

      this.$store.dispatch(`${VUEX_LEARNING_HISTORY_NAMESPACE}/${PERFORMANCE_SUMMARY_REQUEST}`, {
        startDate: dateToYYYYMMDD(this.startDate),
        endDate: dateToYYYYMMDD(this.endDate),
        exerciseType: this.selectedExerciseType
      });
    },
    openAllExerciseLogs() {
      this.$store.commit(
        `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_MUTATION_NAMES.SELECTED_EXERCISE_LOG_TYPE_AND_SKILL}`,
        { exerciseType: 0, exerciseSkill: 0 }
      );
      this.openExerciseLogs();
    },
    openExerciseLogs() {
      this.$router.push({
        name: ROUTES.LEARNING_HISTORY.EXERCISE_LOGS.NAME
      });
    },
    openExerciseLogsFromStatItem(log) {
      this.$store.commit(
        `${VUEX_LEARNING_HISTORY_NAMESPACE}/${VUEX_LEARNING_HISTORY_MUTATION_NAMES.SELECTED_EXERCISE_LOG_TYPE_AND_SKILL}`,
        {
          exerciseType: log.exerciseType,
          exerciseSkill: log.skill
        }
      );
      this.openExerciseLogs();
    },
    weeksAgo(weeksAgo) {
      const today = new Date();
      const oneWeek = 60 * 60 * 24 * 7 * 1000;
      const timeFrame = oneWeek * weeksAgo;
      return new Date(today.valueOf() - timeFrame);
    }
  }
});
